import React from "react";
import { Box, IconButton, TablePagination, Tooltip } from "@mui/material";
import CommonTable, { HeadCell } from "../../../../../business-types/grocery/components/CommonTable";
import ViewListIcon from "@mui/icons-material/ViewList";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "src/business-types/grocery/hooks/tableConfig";
import { ledgerList } from "src/shared/services/interfacev2";
import { formatNumber } from "src/business-types/laundry/modules/orders/OrderSearch";

type LedgerRow = {
  id: string;
  sno: number;
  name: string;
  PhoneNumber: string;
  clientId: string;
  totalOutstandingBalance: string;
  Actions: React.ReactNode;
};

interface LedgerTableProps {
  data: ledgerList[];
  page: number;
  rowsPerPage: number;
  totalRecords: number;
  formatCurrency: (val: number) => string;
  onPageChange: (newPage: number) => void;          
  onRowsPerPageChange: (newRowsPerPage: number) => void; 
  onRowClick: (id: string) => void;
  onSortChange: (sortData: { field: string; orderby: string }) => void;
  sortField?: string;
  sortOrder?: string;
}

const LedgerTable: React.FC<LedgerTableProps> = ({
  data,
  page,
  rowsPerPage,
  totalRecords,
  formatCurrency,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  onSortChange,
}) => {
  // FIX: Don't slice the data - it's already paginated from the server
  // The data prop already contains only the records for the current page
  const rows: LedgerRow[] = data.map((item, index) => ({
    id: item.customerId,
    sno: page * rowsPerPage + index + 1,
    name: [item.firstName, item.middleName, item.lastName].filter(Boolean).join(" "),
    PhoneNumber: item.phones[0]?.phone || "",
    clientId: formatNumber(item.clientId || 0),
    totalOutstandingBalance: formatCurrency(Number(item.totalOutstandingBalance)),
    Actions: (
      <Box display="flex" alignItems="center" gap={1}>
        <Tooltip title="View order">
          <IconButton onClick={() => onRowClick(item.customerId)}>
            <ViewListIcon sx={{ color: "#000" }} />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  }));

  const headCells: HeadCell<LedgerRow>[] = [
    { id: "sno", label: "S.No", align: "left", sorting: false },
    { id: "name", label: "Customer Name", align: "left", sorting: true },
    { id: "PhoneNumber", label: "Phone Number", align: "left", sorting: false },
    { id: "clientId", label: "Client Id", align: "center", sorting: false},
    { id: "totalOutstandingBalance", label: "Outstanding Balance", align: "center", sorting: true },
    { id: "Actions", label: "Actions", align: "center", sorting: false },
  ];

  // Handle sort change from CommonTable
  const handleSortChange = (sortData: { field: string; orderby: string }) => {
    onSortChange(sortData);
  };

  return (
    <div className="w-full overflow-y-auto">
      <CommonTable<LedgerRow>
        data={rows}
        headCells={headCells}
        showToolbar={false}
        denseDefault={false}
        title="Ledger"
        selectable={false}
        pagenation={false}
        pageSize={rowsPerPage}
        className="w-full min-w-[560px]"
        shortByTable={false}
        OrderType={handleSortChange}
      />

      <div className="flex justify-end mr-8 mt-2">
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => onPageChange(newPage)}
          onRowsPerPageChange={(e) => {
            const newSize = parseInt((e.target as HTMLInputElement).value, 10);
            onRowsPerPageChange(newSize);
            onPageChange(0);
          }}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
        />
      </div>
    </div>
  );
};

export default LedgerTable;